import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <SharedHeader 
          transparent={true}
          rightContent={
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          }
        />
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          
          {/* Last Updated */}
          <p className="text-sm text-gray-500 mb-6">
            Last Updated: April 9, 2025
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to Eduzy! These Terms of Service ("Terms") govern your use of the Eduzy 
              educational platform, including our WhatsApp-based learning service and web portal 
              (collectively, the "Service"), operated by Contentium Tech.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              By using our Service, you agree to these Terms. If you are a parent or guardian 
              registering a child, you agree on behalf of yourself and your child.
            </p>
          </section>

          {/* Definitions */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>"Student"</strong> refers to the child enrolled in the Eduzy learning program.</li>
              <li><strong>"Parent"</strong> refers to the parent or legal guardian who registers and manages the Student's account.</li>
              <li><strong>"Subscription"</strong> refers to the paid access plan for using the Service.</li>
              <li><strong>"Free Trial"</strong> refers to the complimentary 90-day access period with limited daily usage.</li>
              <li><strong>"Content"</strong> refers to all educational materials, questions, explanations, and learning resources provided through the Service.</li>
            </ul>
          </section>

          {/* Eligibility */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Eligibility and Parental Consent</h2>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-gray-700 leading-relaxed font-medium">
                Important: Our Service is designed for children in grades 5-8 (ages 10-14).
              </p>
            </div>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Parental Consent Required:</strong> A parent or legal guardian must provide 
                verifiable consent before a child can use our Service, as required by the Digital 
                Personal Data Protection Act, 2023 (DPDP Act).</li>
              <li><strong>Email/Phone Verification:</strong> Parents must verify their email address during 
                registration via a One-Time Password (OTP). Post-registration, OTPs can be sent to 
                both registered email and WhatsApp number.</li>
              <li><strong>Guardian Responsibility:</strong> Parents/guardians are responsible for 
                monitoring their child's use of the Service and ensuring appropriate usage.</li>
              <li><strong>Accurate Information:</strong> Parents must provide accurate information 
                during registration including child's name, age, grade, and school.</li>
              <li><strong>Consent Withdrawal:</strong> Parents may withdraw consent at any time through
                the Parent Dashboard, which will result in termination of the child's account and deletion 
                of their data within 30 days.</li>
            </ul>
          </section>

          {/* Account Security & OTP Verification */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Account Security & OTP Verification</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To protect your child's account and data, all sensitive operations require OTP verification:
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.1 OTP Delivery Channels</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li><strong>Registration:</strong> OTP sent to parent's email address</li>
              <li><strong>Post-Registration:</strong> OTPs can be sent to registered email or WhatsApp number</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.2 Operations Requiring OTP</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Account modifications or profile updates</li>
              <li>Password changes</li>
              <li>Consent withdrawal</li>
              <li>Account deletion requests</li>
              <li>Any admin-initiated changes to student records</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">4.3 Admin-Initiated Changes</h3>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-2">
              <p className="text-gray-700 leading-relaxed">
                If you request an Eduzy administrator to modify or delete your child's records:
              </p>
              <ol className="list-decimal list-inside text-gray-700 space-y-1 ml-4 mt-2">
                <li>A verification OTP will be sent to your registered email/WhatsApp</li>
                <li>You must call our support line at <strong>+91 9654006470</strong></li>
                <li>Provide the OTP to the administrator to authorize the action</li>
                <li>No changes can be made without your verified OTP</li>
              </ol>
            </div>
          </section>

          {/* Account Deletion */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Account Deletion</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-red-700 leading-relaxed font-medium">
                Warning: Account deletion is UNRECOVERABLE. All data will be permanently deleted.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              When you or an admin (with your OTP authorization) deletes an account:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>All personal information will be permanently deleted</li>
              <li>Learning progress, statistics, and assessment history will be removed</li>
              <li>Chat history and interactions will be erased</li>
              <li>Subscription records will be deleted</li>
              <li>This action cannot be undone</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              You will receive a confirmation message before deletion and a final confirmation 
              email after the deletion is complete.
            </p>
          </section>

          {/* Audit Logging */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Audit Logging & Record Retention</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For security, compliance, and dispute resolution purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>All Changes Logged:</strong> Account creations, modifications, deletions, 
                login attempts, and admin operations are recorded</li>
              <li><strong>Retention Period:</strong> Audit logs are retained for <strong>180 days</strong></li>
              <li><strong>Information Recorded:</strong> Timestamp, action type, performer identity, 
                IP address, and OTP verification status</li>
              <li><strong>Legal Access:</strong> Logs may be provided to legal authorities upon valid request</li>
            </ul>
          </section>

          {/* Service Description */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Service Description</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Eduzy provides AI-powered educational services including:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Educational content delivery via WhatsApp in multiple languages (English, Hindi, Punjabi, Tamil, Marathi, Telugu, Kannada)</li>
              <li>AI-generated lessons, questions, and explanations tailored to the student's grade level and selected curriculum board (CBSE or IB)</li>
              <li>Interactive doubt-resolution and Q&A features</li>
              <li>Progress tracking and performance analytics</li>
              <li>Student Web Portal for viewing profile and statistics</li>
              <li>Parent Dashboard for monitoring child's progress and managing consent</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              <strong>Available Subjects:</strong> Mathematics, Science, English, Social Studies, Hindi
            </p>
            <p className="text-gray-700 leading-relaxed mt-2">
              <strong>Supported Curricula:</strong> CBSE and IB (International Baccalaureate) for Grades 5 to 8. The curriculum board is selected per child during registration and can be changed from the Parent Dashboard.
            </p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">7.1 Curriculum Alignment Disclaimer</h3>
            <p className="text-gray-700 leading-relaxed">
              Eduzy uses artificial intelligence to dynamically generate and align educational content to the curriculum board (CBSE or IB) selected for each student. We make every reasonable effort to match our lessons, questions, examples, and terminology to the selected board's framework and grade-level expectations <strong>to the best of our ability</strong>. However, because content is AI-generated and curricula are periodically revised by their respective governing bodies, <strong>occasional variations, omissions, or mismatches between Eduzy's content and the official board syllabus may occur</strong>. Eduzy does not guarantee exact, complete, or exhaustive alignment with any official curriculum, examination board, or school-specific syllabus, and is not affiliated with, endorsed by, or certified by CBSE, the IB Organization, or any examination or accreditation authority. Eduzy's content is intended to supplement, not replace, official school instruction and prescribed textbooks. Parents and students are advised to cross-verify important information against official board materials. To the maximum extent permitted by law, Eduzy shall not be liable for any academic outcome, loss, or consequence arising from reliance on curriculum alignment. You may report a suspected content mismatch to our support team, and we will endeavour to review and improve it.
            </p>
          </section>

          {/* Subscription and Pricing */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Subscription Plans and Pricing</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.1 Free Trial</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Duration: 90 days from registration</li>
              <li>Daily Usage Limit: 30 minutes</li>
              <li>Includes all Pro features</li>
              <li>No payment information required</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.2 Paid Subscriptions</h3>
            <table className="w-full border-collapse border border-gray-300 mt-2 mb-4 text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-3 py-2 text-left">Feature</th>
                  <th className="border border-gray-300 px-3 py-2 text-center">Starter (₹499)</th>
                  <th className="border border-gray-300 px-3 py-2 text-center">Plus (₹699)</th>
                  <th className="border border-gray-300 px-3 py-2 text-center">Pro (₹899)</th>
                </tr>
              </thead>
              <tbody>
                <tr><td className="border border-gray-300 px-3 py-2">Daily Learning Time</td><td className="border border-gray-300 px-3 py-2 text-center">1 hour</td><td className="border border-gray-300 px-3 py-2 text-center">2 hours</td><td className="border border-gray-300 px-3 py-2 text-center">4 hours</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Practice Tests</td><td className="border border-gray-300 px-3 py-2 text-center">1/day</td><td className="border border-gray-300 px-3 py-2 text-center">3/day</td><td className="border border-gray-300 px-3 py-2 text-center">Unlimited</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Test Review &amp; Explanations</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">✓</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Learning Analytics</td><td className="border border-gray-300 px-3 py-2 text-center">Summary</td><td className="border border-gray-300 px-3 py-2 text-center">Full subject mastery</td><td className="border border-gray-300 px-3 py-2 text-center">Full mastery + learning path</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Parent Dashboard</td><td className="border border-gray-300 px-3 py-2 text-center">Basic (last 3 sessions)</td><td className="border border-gray-300 px-3 py-2 text-center">Full (last 10, current concept)</td><td className="border border-gray-300 px-3 py-2 text-center">Premium (unlimited, full path)</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Leaderboard</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">✓</td><td className="border border-gray-300 px-3 py-2 text-center">✓ + Percentile</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Progress Reports</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">Weekly</td><td className="border border-gray-300 px-3 py-2 text-center">Daily + Quarterly</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Progress Certificates</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">✓</td><td className="border border-gray-300 px-3 py-2 text-center">✓</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">AI Study Plans</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">✓</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Daily Goals &amp; Streak Nudges</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">✓</td><td className="border border-gray-300 px-3 py-2 text-center">✓</td></tr>
                <tr><td className="border border-gray-300 px-3 py-2">Grace Period</td><td className="border border-gray-300 px-3 py-2 text-center">✗</td><td className="border border-gray-300 px-3 py-2 text-center">7 days</td><td className="border border-gray-300 px-3 py-2 text-center">7 days</td></tr>
              </tbody>
            </table>
            <p className="text-gray-700 text-sm italic">All prices are per subject per month in INR.</p>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">8.3 Payment Terms</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>All prices are in Indian Rupees (INR) and inclusive of applicable taxes.</li>
              <li>Payment is due at the start of each billing cycle.</li>
              <li>We accept payments via UPI, Credit/Debit Cards, and Net Banking.</li>
              <li>Subscriptions auto-renew unless cancelled before the renewal date.</li>
            </ul>
          </section>

          {/* Usage Limits */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Usage Limits and Fair Use</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Daily usage limits are enforced to ensure quality service for all users.</li>
              <li>Unused daily time does not carry over to the next day.</li>
              <li>Usage is tracked from midnight to midnight Indian Standard Time (IST).</li>
              <li>Attempting to circumvent usage limits may result in account suspension.</li>
            </ul>
          </section>

          {/* Cancellation and Refunds */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Cancellation and Refunds</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">10.1 Free Trial</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>All new users receive a 90-day free trial with no payment required.</li>
              <li>You may cancel the trial at any time with no charges.</li>
              <li>No automatic conversion to paid subscription without your explicit consent.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">10.2 Cancellation</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>You may cancel your subscription at any time through the Parent Dashboard.</li>
              <li>Cancellation stops future auto-renewals but does not provide a refund.</li>
              <li>You will retain access until the end of the current paid period.</li>
              <li>Learning progress and data are preserved for 90 days after subscription ends.</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">10.3 Refund Policy</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li><strong>No Refunds:</strong> Since we offer a generous 90-day free trial, we do not offer refunds on paid subscriptions.</li>
              <li><strong>Exceptions:</strong> Refunds may be considered for billing errors, duplicate charges, or verified technical issues preventing service access.</li>
              <li>Refund requests for exceptional circumstances must be submitted to <a href="mailto:support@contentiumtech.com" className="text-emerald-600 hover:underline">support@contentiumtech.com</a>.</li>
              <li>Approved refunds are processed within 5-7 business days.</li>
            </ul>
            
            <p className="text-gray-700 mt-4">
              For full details, please see our <a href="/refund" className="text-emerald-600 hover:underline">Refund & Cancellation Policy</a>.
            </p>
          </section>

          {/* User Responsibilities */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. User Responsibilities</h2>
            <p className="text-gray-700 leading-relaxed mb-4">Users agree to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Use the Service only for legitimate educational purposes</li>
              <li>Not share account credentials or allow unauthorized access</li>
              <li>Not attempt to reverse-engineer, hack, or exploit the Service</li>
              <li>Not use the Service to send spam, inappropriate, or harmful content</li>
              <li>Respect intellectual property rights of Eduzy and third parties</li>
              <li>Report any security vulnerabilities or bugs to our support team</li>
              <li>Keep contact information up to date</li>
            </ul>
          </section>

          {/* Content */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Educational Content</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>AI-Generated:</strong> Our content is generated using artificial intelligence 
                and is designed to be educational and age-appropriate for grades 5-8.</li>
              <li><strong>Supplementary Nature:</strong> Eduzy content is meant to supplement formal 
                education, not replace school curriculum or teacher instruction.</li>
              <li><strong>Accuracy:</strong> While we strive for accuracy, we do not guarantee that all 
                content is error-free. Parents and students should verify important information.</li>
              <li><strong>Intellectual Property:</strong> All content, including text, graphics, and 
                software, is owned by Contentium Tech and protected by copyright laws.</li>
              <li><strong>No Redistribution:</strong> Content may not be copied, distributed, or 
                used for commercial purposes without written permission.</li>
            </ul>
          </section>

          {/* Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Your privacy is important to us. Please review our{" "}
              <Link to="/privacy" className="text-emerald-600 hover:underline font-medium">
                Privacy Policy
              </Link>{" "}
              to understand how we collect, use, and protect your data in compliance with the 
              Digital Personal Data Protection Act, 2023 (DPDP Act).
            </p>
          </section>

          {/* Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the maximum extent permitted by law:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>Eduzy is provided "as is" and "as available" without warranties of any kind.</li>
              <li>We do not guarantee uninterrupted or error-free service.</li>
              <li>AI-generated content may contain inaccuracies and may not fully or exactly align with the official CBSE or IB syllabus (see Section 7.1). We are not liable for any curriculum mismatch or resulting academic consequence.</li>
              <li>We are not liable for any indirect, incidental, special, or consequential damages.</li>
              <li>We are not responsible for academic outcomes or examination results.</li>
              <li>Our total liability shall not exceed the amount paid by you in the last 12 months.</li>
            </ul>
          </section>

          {/* Indemnification */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify and hold harmless Contentium Tech, its officers, 
              directors, employees, and agents from any claims, damages, losses, or expenses 
              arising from your use of the Service or violation of these Terms.
            </p>
          </section>

          {/* Termination */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We may suspend or terminate accounts that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
              <li>Violate these Terms of Service</li>
              <li>Engage in fraudulent, abusive, or illegal activity</li>
              <li>Misuse the Service or attempt to circumvent restrictions</li>
              <li>Provide false information during registration</li>
              <li>Have unpaid subscription fees for more than 30 days</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              Upon termination, your right to use the Service ceases immediately. Data will be 
              handled as per our Privacy Policy and applicable laws.
            </p>
          </section>

          {/* Changes */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We may modify these Terms at any time. Material changes will be notified via 
              WhatsApp, email, or through the Parent Dashboard at least 15 days before taking 
              effect. Continued use after the effective date constitutes acceptance of the new Terms. 
              If you do not agree with the changes, you may cancel your subscription before 
              the effective date.
            </p>
          </section>

          {/* Governing Law */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Governing Law and Disputes</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms are governed by the laws of India. Any disputes shall be subject to 
              the exclusive jurisdiction of the courts in Delhi, India.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Before initiating legal proceedings, parties agree to attempt resolution through 
              good-faith negotiation for at least 30 days.
            </p>
          </section>

          {/* Severability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be unenforceable, the remaining 
              provisions shall continue in full force and effect.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">20. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed">
              For questions, concerns, or complaints about these Terms, please contact us at:
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mt-2">
              <p className="text-gray-700"><strong>Contentium Tech</strong></p>
              <p className="text-gray-600 text-sm">Parent Company of Eduzy</p>
              <p className="text-gray-700 mt-2">Email: <a href="mailto:info@contentiumtech.com" className="text-emerald-600 hover:underline">info@contentiumtech.com</a></p>
              <p className="text-gray-700">Phone: <a href="tel:+919654006470" className="text-emerald-600 hover:underline">+91 9654006470</a></p>
              <p className="text-gray-700">WhatsApp: Send "HELP" to our number</p>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-center text-gray-500 text-sm">
              © 2025 Eduzy by Contentium Tech. All rights reserved.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
